import { Injectable } from '@angular/core';
import { ApiService } from './api.service';

/**
 * Keeps the session alive for as long as the user is active and ends it when
 * the refresh token says so. There is no client-side idle timer and no
 * SESSION-TIMEOUT setting: when the admin clicks, types or returns to the tab
 * and the access token is about to expire, the refresh token is checked
 * (`ApiService.renewToken`). The server rejects an expired or used refresh
 * token, and only then is the user logged out.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private static readonly ACTIVITY_CHECK_INTERVAL_MS = 5000;
  private lastCheck = 0;
  private listening = false;

  constructor(private readonly apiService: ApiService) {}

  setupSessionTimeout() {
    if (!this.listening) {
      this.listening = true;
      this.addListeners();
    }
    this.checkRefreshToken();
  }

  private addListeners(): void {
    ['click', 'keydown', 'touchstart'].forEach(event => {
      window.addEventListener(event, () => this.onActivity());
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkRefreshToken();
      }
    });
  }

  private onActivity(): void {
    const now = Date.now();
    if (now - this.lastCheck >= SessionTimeoutService.ACTIVITY_CHECK_INTERVAL_MS) {
      this.lastCheck = now;
      this.checkRefreshToken();
    }
  }

  private checkRefreshToken(): void {
    if (this.apiService.getUserToken() && this.apiService.isAboutToExpire()) {
      // A 401 from the server logs the user out inside ApiService.
      this.apiService.renewToken().subscribe({ error: () => undefined });
    }
  }
}
